import { FedexService } from '../../enums/label-providers/fedex/fedex-service';
import { GlobalPostService } from '../../enums/label-providers/globalpost/globalpost-service';
import { PackageType } from '../../enums/package-type';
import { UpsBYOAService } from '../../enums/label-providers/ups/ups-byoa-service';
import { UpsWalletService } from '../../enums/label-providers/ups/ups-wallet-service';
import { UspsWalletService } from '../../enums/label-providers/usps/usps-wallet-service';
import { createSelector } from 'reselect';
import { FeatureFlags } from '../../feature-flags/feature-flags';
import { DhlEcommerceService } from '../../enums/label-providers/dhl/dhl-ecommerce-service';
import { DhlEcommerceWalletedService } from '../../enums/label-providers/dhl/dhl-ecommerce-walleted-service';
import { FedexWalletService } from '../../enums/label-providers/fedex/fedex-walleted-service';
import { AmazonBuyShippingService } from '../../enums/label-providers/amazon/amazon-buy-shipping-service';

// All package types valid
const ALL_PACKAGE_TYPES = [];

const fromUspsServiceMap = {
  [UspsWalletService.FirstClassMail]: {
    mappedServiceIds: [UpsWalletService.GroundSaver, UpsWalletService.Ground],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [UspsWalletService.ParcelSelect]: {
    mappedServiceIds: [UpsWalletService.GroundSaver, UpsWalletService.Ground],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [UspsWalletService.PriorityMail]: {
    mappedServiceIds: [UpsWalletService.Ground],
    validPackageTypeIds: [PackageType.ThickEnvelope, PackageType.StandardPackage],
  },
  [UspsWalletService.GroundAdvantage]: {
    mappedServiceIds: [UpsWalletService.GroundSaver, UpsWalletService.Ground],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
};

const fromFedexServiceMap = {
  [FedexService.Ground]: {
    mappedServiceIds: [UpsWalletService.Ground, UpsWalletService.GroundSaver, UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.HomeDelivery]: {
    mappedServiceIds: [UpsWalletService.Ground, UpsWalletService.GroundSaver, UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
};

const fromFedexServiceMapExtended = {
  [FedexService.ExpressSaver]: {
    mappedServiceIds: [FedexWalletService.Economy, FedexWalletService.ExpressSaverOneRate],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.Ground]: {
    mappedServiceIds: [FedexWalletService.Ground],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.HomeDelivery]: {
    mappedServiceIds: [FedexWalletService.HomeDelivery],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.TwoDay]: {
    mappedServiceIds: [FedexWalletService.TwoDay, FedexWalletService.TwoDayOneRate],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.StandardOvernight]: {
    mappedServiceIds: [FedexWalletService.StandardOvernight, FedexWalletService.StandardOvernightOneRate],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.PriorityOvernight]: {
    mappedServiceIds: [FedexWalletService.PriorityOvernight, FedexWalletService.PriorityOvernightOneRate],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.SmartPostParcelSelect]: {
    mappedServiceIds: [FedexWalletService.GroundEconomyParcelSelect],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.SmartPostParcelSelectLightWeight]: {
    mappedServiceIds: [FedexWalletService.GroundEconomyParcelSelect],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.InternationalEconomy]: {
    mappedServiceIds: [FedexWalletService.InternationalEconomy],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.InternationalPriority]: {
    mappedServiceIds: [FedexWalletService.InternationalPriority],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.ConnectPlus]: {
    mappedServiceIds: [FedexWalletService.InternationalConnectPlus],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
};

const fromUpsServiceMap = {
  [UpsBYOAService.UPSSurePostLessthan1lb]: {
    mappedServiceIds: [UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [UpsBYOAService.UPSSurePost1lborGreater]: {
    mappedServiceIds: [UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [UpsBYOAService.UPSSurePostBPM]: {
    mappedServiceIds: [UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [UpsBYOAService.UPSSurePostMedia]: {
    mappedServiceIds: [UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [UpsBYOAService.UPSGround]: {
    mappedServiceIds: [UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [UpsBYOAService.SecondDayAir]: {
    mappedServiceIds: [UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
};

const fromAmazonBuyShippingServiceMap = {
  [AmazonBuyShippingService.UspsPriorityMail]: {
    mappedServiceIds: [UspsWalletService.PriorityMail],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.UspsGroundAdvantageLessThan1Lb]: {
    mappedServiceIds: [UspsWalletService.GroundAdvantage],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.UspsGroundAdvantage1lbTo70lb]: {
    mappedServiceIds: [UspsWalletService.GroundAdvantage],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.UspsGroundAdvantageCubic]: {
    mappedServiceIds: [UspsWalletService.GroundAdvantage],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.FedexGround]: {
    mappedServiceIds: [FedexWalletService.Ground],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.FedexHomeDelivery]: {
    mappedServiceIds: [FedexWalletService.HomeDelivery],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.Fedex2Day]: {
    mappedServiceIds: [FedexWalletService.TwoDay, FedexWalletService.TwoDayOneRate],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.FedexStandardOvernight]: {
    mappedServiceIds: [FedexWalletService.StandardOvernight, FedexWalletService.StandardOvernightOneRate],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.FedexSecondDayOneRate]: {
    mappedServiceIds: [FedexWalletService.TwoDayOneRate],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.UpsGround]: {
    mappedServiceIds: [UpsWalletService.Ground],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.Ups3DaySelect]: {
    mappedServiceIds: [UpsWalletService.ThreeDaySelect],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.Ups2ndDayAir]: {
    mappedServiceIds: [UpsWalletService.SecondDayAir],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.UpsNextDayAirSaver]: {
    mappedServiceIds: [UpsWalletService.NextDayAirSaver],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [AmazonBuyShippingService.UpsNextDayAir]: {
    mappedServiceIds: [UpsWalletService.NextDayAir],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
};

const toGlobalPostServiceMap = {
  [UpsBYOAService.Standard]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [UpsBYOAService.GroundInternational]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.InternationalGround]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.InternationalEconomy]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [FedexService.ConnectPlus]: {
    mappedServiceIds: [GlobalPostService.Plus, UspsWalletService.GlobalPostPlus],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [DhlEcommerceService.ParcelInternationalDirectDdp]: {
    mappedServiceIds: [GlobalPostService.Plus, UspsWalletService.GlobalPostPlus],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [DhlEcommerceWalletedService.ParcelInternationalDirectDdp]: {
    mappedServiceIds: [GlobalPostService.Plus, UspsWalletService.GlobalPostPlus],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [DhlEcommerceService.ParcelInternationalStandard]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [DhlEcommerceService.PacketInternational]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [DhlEcommerceService.ParcelInternationalDirectDdu]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [DhlEcommerceService.PacketPlusInternational]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [DhlEcommerceService.GlobalMailPacketIpa]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
  [DhlEcommerceWalletedService.ParcelInternationalStandardDdu]: {
    mappedServiceIds: [GlobalPostService.StandardIntl, UspsWalletService.GlobalPostStandardIntl],
    validPackageTypeIds: ALL_PACKAGE_TYPES,
  },
};

const addServiceMapping = (existingServiceMapEntry, proposedServiceMapEntry) => {
  if (!existingServiceMapEntry) {
    return proposedServiceMapEntry;
  }

  const { mappedServiceIds: existingMappedServiceIds, validPackageTypeIds: existingValidPackageTypeIds } = existingServiceMapEntry;
  const { mappedServiceIds: proposedMappedServiceIds, validPackageTypeIds: proposedValidPackageTypeIds } = proposedServiceMapEntry;
  const mappedServiceIds = [...new Set([...existingMappedServiceIds, ...proposedMappedServiceIds])];
  const validPackageTypeIds = [...new Set([...existingValidPackageTypeIds, ...proposedValidPackageTypeIds])];
  return {
    mappedServiceIds,
    validPackageTypeIds,
  };
};

const addServiceMapGrouping = (serviceMap, serviceMapGroup) => {
  const serviceIds = Object.keys(serviceMapGroup);
  return serviceIds.reduce((accServiceMap, serviceId) => {
    const proposedServiceMapEntry = serviceMapGroup[serviceId];
    const existingServiceMapEntry = accServiceMap[serviceId];

    const updatedServiceMapEntry = addServiceMapping(existingServiceMapEntry, proposedServiceMapEntry);
    return {
      ...accServiceMap,
      [serviceId]: updatedServiceMapEntry,
    };
  }, serviceMap);
};

/** @deprecated */
export const enabledShippingServiceRecommendationMapSelector = createSelector(
  (state) => new FeatureFlags({ state }).enableUspsRateComparison,
  (state) => new FeatureFlags({ state }).enableFedexRateComparison,
  (state) => new FeatureFlags({ state }).enableFedexWalletServiceMappings,
  (enableUspsRateComparison, enableFedexRateComparison, enableFedexWalletServiceMappings) => {
    const serviceMaps = [
      enableUspsRateComparison ? fromUspsServiceMap : {},
      enableFedexRateComparison ? fromFedexServiceMap : {},
      fromUpsServiceMap,
      toGlobalPostServiceMap,
      enableFedexWalletServiceMappings ? fromFedexServiceMapExtended : {},
      fromAmazonBuyShippingServiceMap,
    ];

    return serviceMaps.reduce((accServiceMap, serviceMapGroup) => {
      return addServiceMapGrouping(accServiceMap, serviceMapGroup);
    }, {});
  },
);
